import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClienteSearchSelect } from "@/components/ClienteSearchSelect";
import { useAuth } from "@/hooks/useAuth";
import {
  useAtualizarTarefa,
  useCriarTarefa,
  useExcluirTarefa,
  usePerfisAtivosTarefas,
} from "@/hooks/useTarefas";
import {
  TAREFA_PRIORIDADE_LABEL,
  TAREFA_STATUS_LABEL,
  type Tarefa,
  type TarefaPrioridade,
  type TarefaStatus,
} from "@/types/tarefas";
import { fromDatetimeLocal, toDatetimeLocal } from "./tarefasUtils";
import { Loader2, Trash2 } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tarefa?: Tarefa | null;
  /** Pré-preenche prazo ao criar a partir do calendário */
  defaultPrazo?: string | null;
};

export function TarefaFormDialog({
  open,
  onOpenChange,
  tarefa,
  defaultPrazo,
}: Props) {
  const { user } = useAuth();
  const { data: perfis = [] } = usePerfisAtivosTarefas();
  const criar = useCriarTarefa();
  const atualizar = useAtualizarTarefa();
  const excluir = useExcluirTarefa();
  const editing = !!tarefa;

  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [status, setStatus] = useState<TarefaStatus>("a_fazer");
  const [prioridade, setPrioridade] = useState<TarefaPrioridade>("media");
  const [prazoLocal, setPrazoLocal] = useState("");
  const [responsavelId, setResponsavelId] = useState("");
  const [participanteIds, setParticipanteIds] = useState<string[]>([]);
  const [clienteId, setClienteId] = useState("");
  const [interna, setInterna] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (tarefa) {
      setTitulo(tarefa.titulo);
      setDescricao(tarefa.descricao ?? "");
      setStatus(tarefa.status);
      setPrioridade(tarefa.prioridade);
      setPrazoLocal(toDatetimeLocal(tarefa.prazo));
      setResponsavelId(tarefa.responsavel_id);
      setParticipanteIds(
        (tarefa.participantes ?? []).map((p) => p.user_id).filter((id) => id !== tarefa.responsavel_id)
      );
      setClienteId(tarefa.cliente_id ? String(tarefa.cliente_id) : "");
      setInterna(!tarefa.cliente_id);
    } else {
      setTitulo("");
      setDescricao("");
      setStatus("a_fazer");
      setPrioridade("media");
      setPrazoLocal(toDatetimeLocal(defaultPrazo ?? null));
      setResponsavelId(user?.id ?? "");
      setParticipanteIds([]);
      setClienteId("");
      setInterna(true);
    }
  }, [open, tarefa, defaultPrazo, user?.id]);

  const participantesDisponiveis = useMemo(
    () => perfis.filter((p) => p.id !== responsavelId),
    [perfis, responsavelId]
  );

  const saving = criar.isPending || atualizar.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim() || !responsavelId) return;

    const payload = {
      titulo: titulo.trim(),
      descricao: descricao.trim() || null,
      status,
      prioridade,
      prazo: fromDatetimeLocal(prazoLocal),
      responsavel_id: responsavelId,
      cliente_id: interna || !clienteId ? null : parseInt(clienteId, 10),
      participante_ids: participanteIds.filter((id) => id !== responsavelId),
    };

    if (editing && tarefa) {
      atualizar.mutate(
        { id: tarefa.id, ...payload },
        { onSuccess: () => onOpenChange(false) }
      );
    } else {
      criar.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  };

  const toggleParticipante = (id: string) => {
    setParticipanteIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar tarefa" : "Nova tarefa"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="titulo">Título *</Label>
            <Input
              id="titulo"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Ligar para o cliente sobre renovação"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={3}
              placeholder="Detalhes, lembretes..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as TarefaStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TAREFA_STATUS_LABEL) as TarefaStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {TAREFA_STATUS_LABEL[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select
                value={prioridade}
                onValueChange={(v) => setPrioridade(v as TarefaPrioridade)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TAREFA_PRIORIDADE_LABEL) as TarefaPrioridade[]).map((p) => (
                    <SelectItem key={p} value={p}>
                      {TAREFA_PRIORIDADE_LABEL[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="prazo">Prazo</Label>
            <Input
              id="prazo"
              type="datetime-local"
              value={prazoLocal}
              onChange={(e) => setPrazoLocal(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Responsável *</Label>
            <Select value={responsavelId} onValueChange={setResponsavelId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {perfis.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Participantes</Label>
            <div className="max-h-32 overflow-y-auto rounded-md border border-input p-2 space-y-1">
              {participantesDisponiveis.length === 0 ? (
                <p className="text-xs text-muted-foreground px-1">Nenhum outro usuário ativo</p>
              ) : (
                participantesDisponiveis.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-2 text-sm px-1 py-0.5 rounded hover:bg-muted/60 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={participanteIds.includes(p.id)}
                      onChange={() => toggleParticipante(p.id)}
                      className="rounded border-input"
                    />
                    {p.nome}
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={interna}
                onChange={(e) => {
                  setInterna(e.target.checked);
                  if (e.target.checked) setClienteId("");
                }}
                className="rounded border-input"
              />
              Tarefa interna (sem cliente)
            </label>
            {!interna && (
              <ClienteSearchSelect
                value={clienteId}
                onChange={(id) => setClienteId(id)}
                label="Cliente"
              />
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            {editing && tarefa && (
              <Button
                type="button"
                variant="destructive"
                className="mr-auto"
                disabled={excluir.isPending}
                onClick={() => {
                  if (!confirm("Excluir esta tarefa?")) return;
                  excluir.mutate(tarefa.id, { onSuccess: () => onOpenChange(false) });
                }}
              >
                {excluir.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !titulo.trim() || !responsavelId}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
